'use client';

import { useRef, useCallback, useEffect } from 'react';

// ─────────────────────────────────────────────────────────────
//  🎵  SURFSTERRE — GAME AUDIO HOOK
//  Reads audio URLs from the admin config stored in localStorage.
//  Falls back to empty string (silent) if no URL is configured.
// ─────────────────────────────────────────────────────────────

const STORAGE_KEY = 'surfsterre_admin_config';

export type AudioTrack =
  | 'loading'    // loading / permission screen
  | 'menu'       // area select screen
  | 'gameplay'   // per-level gameplay (falls back to level-specific)
  | 'wipeout'    // wipeout sting (short, one-shot)
  | 'combo';     // combo sting (short, one-shot)

interface AudioConfig {
  loadingMusic: string;
  menuMusic: string;
  gameplayMusic: Record<string, string>; // keyed by area id or 'default'
  wipeoutSting: string;
  comboSting: string;
}

function loadAudioConfig(): AudioConfig {
  if (typeof window === 'undefined') return buildDefaultAudio();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        loadingMusic: parsed.loadingMusic ?? '',
        menuMusic: parsed.menuMusic ?? '',
        gameplayMusic: parsed.gameplayMusic ?? {},
        wipeoutSting: parsed.wipeoutSting ?? '',
        comboSting: parsed.comboSting ?? '',
      };
    }
  } catch {}
  return buildDefaultAudio();
}

function buildDefaultAudio(): AudioConfig {
  return {
    loadingMusic: '',
    menuMusic: '',
    gameplayMusic: {},
    wipeoutSting: '',
    comboSting: '',
  };
}

export function useGameAudio() {
  const bgmRef = useRef<HTMLAudioElement | null>(null);
  const stingRef = useRef<HTMLAudioElement | null>(null);
  const currentTrackRef = useRef<string>('');
  const mutedRef = useRef<boolean>(false);

  // ── Create or reuse audio element
  const getOrCreateBGM = useCallback((): HTMLAudioElement => {
    if (!bgmRef.current) {
      const audio = new Audio();
      audio.loop = true;
      audio.volume = 0.45;
      bgmRef.current = audio;
    }
    return bgmRef.current;
  }, []);

  // ── Play a background music track (loops)
  const playBGM = useCallback((url: string) => {
    if (!url || typeof window === 'undefined') return;
    if (currentTrackRef.current === url) return; // already playing this track

    const audio = getOrCreateBGM();
    currentTrackRef.current = url;

    // Fade out current track if playing
    if (!audio.paused) {
      audio.pause();
    }

    audio.src = url;
    audio.muted = mutedRef.current;
    audio.load();
    audio.play().catch(err => {
      console.warn('[GameAudio] BGM play blocked:', err.message, '— will retry on next user interaction');
    });
  }, [getOrCreateBGM]);

  // ── Stop background music
  const stopBGM = useCallback(() => {
    if (bgmRef.current && !bgmRef.current.paused) {
      bgmRef.current.pause();
      bgmRef.current.currentTime = 0;
    }
    currentTrackRef.current = '';
  }, []);

  // ── Play a one-shot sting (wipeout, combo)
  const playSting = useCallback((url: string) => {
    if (!url || typeof window === 'undefined') return;
    if (stingRef.current) {
      stingRef.current.pause();
    }
    const audio = new Audio(url);
    audio.volume = 0.7;
    audio.muted = mutedRef.current;
    stingRef.current = audio;
    audio.play().catch(err => {
      console.warn('[GameAudio] Sting play blocked:', err.message);
    });
  }, []);

  // ── Toggle mute
  const toggleMute = useCallback(() => {
    mutedRef.current = !mutedRef.current;
    if (bgmRef.current) bgmRef.current.muted = mutedRef.current;
    if (stingRef.current) stingRef.current.muted = mutedRef.current;
    return mutedRef.current;
  }, []);

  const isMuted = useCallback(() => mutedRef.current, []);

  // ── High-level: play track by game state
  const playTrackForState = useCallback(
    (state: 'loading' | 'menu' | 'running' | 'crashed', areaId?: string) => {
      const cfg = loadAudioConfig();
      if (state === 'loading') {
        playBGM(cfg.loadingMusic);
      } else if (state === 'menu') {
        playBGM(cfg.menuMusic);
      } else if (state === 'running') {
        // Try area-specific track first, then 'default', then first available
        const areaTrack = areaId ? cfg.gameplayMusic[areaId] : '';
        const defaultTrack = cfg.gameplayMusic['default'] ?? '';
        const fallback = Object.values(cfg.gameplayMusic)[0] ?? '';
        playBGM(areaTrack || defaultTrack || fallback);
      } else if (state === 'crashed') {
        playSting(cfg.wipeoutSting);
      }
    },
    [playBGM, playSting]
  );

  const playComboSting = useCallback(() => {
    const cfg = loadAudioConfig();
    playSting(cfg.comboSting);
  }, [playSting]);

  const playWipeoutSting = useCallback(() => {
    const cfg = loadAudioConfig();
    playSting(cfg.wipeoutSting);
  }, [playSting]);

  // ── Cleanup on unmount
  useEffect(() => {
    return () => {
      if (bgmRef.current) {
        bgmRef.current.pause();
        bgmRef.current.src = '';
      }
    };
  }, []);

  return {
    playBGM,
    stopBGM,
    playSting,
    playTrackForState,
    playComboSting,
    playWipeoutSting,
    toggleMute,
    isMuted,
  };
}
